import React from 'react';
import { Alert } from 'reactstrap';

import { formatDateString } from '~libs/dayjs';

import { Button } from '~components';

import { CutoverMandate } from '~support-at-home/actions/sahServiceCutover';

interface ComponentProps {
  mandate?: CutoverMandate;
  /**
   * Opens the assistant with the mandated change already stated on its first step.
   *
   * Async because the caller re-reads the mandate before it prefills - the pairs depend on the
   * associations on the screen behind this, and those can have been edited since it was last read.
   */
  onStart: () => void | Promise<void>;
  disabled?: boolean;
}

/**
 * What Services Australia has required, stated where a provider comes to do it.
 *
 * The assistant will move any service in any category to any other, which is deliberate - a provider
 * has their own changes to make and this is where they make them. What that flexibility costs is that
 * the assistant states none of the mandated change itself, so a provider carrying it out has to
 * arrive already knowing which services move and which category they move to. A run assembled from
 * memory can be wrong in either direction and look no different on screen: moving personal care into
 * Everyday Living, or reversing the two ends of the move, both produce a perfectly valid-looking run.
 *
 * So the mandate is stated here, and offered ready-made. The action fills the first step in rather
 * than starting the run - the provider still reads the pairs, still confirms every step - because
 * this removes the remembering, not the deciding.
 *
 * Nothing renders when there is nothing to offer. A company with no personal care associations, or
 * reference data not yet split into periods, has no mandated change to carry out, and a banner
 * announcing one they cannot action would be worse than none.
 */
const CutoverMandateBanner: React.FC<ComponentProps> = ({ mandate, onStart, disabled = false }) => {
  const { cutover_date, from_category, to_category, sah_service_type_name, pairs = [] } = mandate ?? {};

  if (!from_category || !to_category || pairs.length === 0) return null;

  const services = sah_service_type_name ? `${sah_service_type_name} services` : 'Personal care services';

  return (
    <Alert color="warning" className="sah-cutover-mandate-banner" data-cy="sah-cutover-mandate-banner">
      <div className="sah-cutover-mandate-text">
        <b>
          {services} move from {from_category.name} to {to_category.name} on{' '}
          {cutover_date ? formatDateString(cutover_date) : ''}.
        </b>
        <div>
          {pairs.length} of your services {pairs.length === 1 ? 'is' : 'are'} affected. The assistant can also be used
          for any other service change your business needs - this is one use of it, not the only one.
        </div>
      </div>
      <Button
        id="sah-cutover-mandate-start"
        dataCy="sah-cutover-mandate-start"
        color="success"
        disabled={disabled}
        onClick={onStart}
      >
        Start the Cutover Assistant
      </Button>
    </Alert>
  );
};

export default CutoverMandateBanner;
